import { useEffect, useState } from 'react';
import { supabase } from '@saas-boilerplate/shared';

/**
 * Whether the signed-in user has the Upstand desktop app (docs/PRD_DEVICE_AUTO_RECORD.md §4.3).
 *
 * Reads desktop_devices directly: migration 0105's RLS already allows a user to SELECT their own
 * device rows (and admins to read the workspace's, for the kill switch), so no edge function is
 * needed. Deliberately user-scoped rather than workspace-scoped - "you don't have the app yet" is
 * the actionable message, and a non-admin cannot see teammates' devices anyway.
 */
export type DesktopDevice = {
  id: string;
  name: string;
  platform: string;
  app_version: string | null;
  last_seen_at: string;
  disabled_at: string | null;
};

/** A device is "online" if it has heartbeat within this window (desktop-app-state beats every 45s). */
const ONLINE_WINDOW_MS = 10 * 60 * 1000;

export function useDesktopApp(workspaceId: string | null | undefined) {
  const [devices, setDevices] = useState<DesktopDevice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!workspaceId) {
      setDevices([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    void (async () => {
      const { data } = await supabase
        .from('desktop_devices')
        .select('id, name, platform, app_version, last_seen_at, disabled_at')
        .eq('workspace_id', workspaceId)
        .order('last_seen_at', { ascending: false });
      if (cancelled) return;
      setDevices((data as DesktopDevice[] | null) || []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const active = devices.filter((d) => !d.disabled_at);
  const online = active.filter((d) => Date.now() - new Date(d.last_seen_at).getTime() < ONLINE_WINDOW_MS);

  return {
    loading,
    devices,
    /** At least one device registered and not disabled by an admin. */
    installed: active.length > 0,
    /** At least one device has beaten in the last 10 minutes. */
    online: online.length > 0,
  };
}
